import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./RouteProgress.scss";

/**
 * NProgress-style top-of-page indicator that fires on every route change.
 *
 * The problem this solves:
 *   - Routes are lazy-loaded; click → URL flips → Suspense fires → next
 *     component mounts → its useEffect fetches data → only then does the
 *     visible content actually change.
 *   - During that ~200–2000 ms gap, the user stares at the OLD page and
 *     reasonably concludes their click missed.
 *
 * What the bar does:
 *   - On every `location.pathname` change, snap to a baseline width and
 *     start "trickling" toward 90% over ~600 ms. This gives instant
 *     visual feedback that something is happening — even before the
 *     lazy chunk has resolved.
 *   - When the new route's mount cycle settles (one rAF after the next
 *     pathname is committed) we trip to 100% and fade out.
 *
 * Single source of truth for navigation feedback — global, dependency-free,
 * doesn't poke at any individual route's loading state.
 */
const TRICKLE_TARGET = 90;
const TRICKLE_INTERVAL_MS = 80;
const COMPLETE_DELAY_MS = 250;
const FADE_OUT_MS = 200;

export default function RouteProgress() {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const trickleTimer = useRef(null);
  const finishTimer = useRef(null);
  const fadeTimer = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip the synthetic first mount — we don't want a phantom bar
    // crossing the screen on initial app load (the splash / Suspense
    // fallback already covers that).
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    clearTimeout(finishTimer.current);
    clearTimeout(fadeTimer.current);
    clearInterval(trickleTimer.current);

    setVisible(true);
    setProgress(8);

    trickleTimer.current = setInterval(() => {
      setProgress((p) => {
        if (p >= TRICKLE_TARGET) {
          clearInterval(trickleTimer.current);
          return p;
        }
        // Ease-out: large jumps early, smaller as we approach the cap.
        // Keeps the bar feeling responsive without ever pretending to
        // be "done" before the new route actually paints.
        const remaining = TRICKLE_TARGET - p;
        return Math.min(TRICKLE_TARGET, p + remaining * 0.18);
      });
    }, TRICKLE_INTERVAL_MS);

    // Hand off to "complete" after a short post-mount tick. The new
    // route will have committed by then; if its data fetch is still in
    // flight, that route owns its own loader (e.g. MainLoader).
    finishTimer.current = setTimeout(() => {
      clearInterval(trickleTimer.current);
      setProgress(100);
      fadeTimer.current = setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, FADE_OUT_MS);
    }, COMPLETE_DELAY_MS);

    return () => {
      clearInterval(trickleTimer.current);
      clearTimeout(finishTimer.current);
      clearTimeout(fadeTimer.current);
    };
  }, [location.pathname]);

  return (
    <div
      className={`route-progress ${visible ? "route-progress--visible" : ""}`}
      aria-hidden="true"
    >
      <div
        className="route-progress__bar"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}
